import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/feature/StructuredData';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = articles.filter((a) => a.categoryId === category.id);

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.naishuimanhua.com/' },
          { '@type': 'ListItem', position: 2, name: category.name, item: `https://www.naishuimanhua.com/category/${category.slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${category.name} - 奶水漫画`,
        description: category.description,
        isPartOf: { '@type': 'WebSite', name: '奶水漫画' },
        about: { '@type': 'Thing', name: category.name },
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: categoryArticles.map((article, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Article',
              name: article.title,
              url: `https://www.naishuimanhua.com/article/${article.slug}`,
            },
          })),
        },
      },
    ],
  };

  return (
    <>
      <StructuredData data={structuredData} />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category.name },
            ]}
          />

          <div className="py-8 md:py-12">
            <div className="flex items-start gap-4 mb-8">
              <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-xl text-2xl" style={{ backgroundColor: 'oklch(var(--accent-100))', color: 'oklch(var(--accent-600))' }}>
                <i className={category.icon}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 max-w-xl">
                  {category.description}
                </p>
                <p className="text-xs text-foreground-500 mt-2">
                  共 {category.articleCount} 篇漫画
                </p>
              </div>
            </div>

            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无漫画内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-accent-200 transition-all duration-300 no-underline"
                  >
                    <div className="relative w-full h-[180px] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        title={`${article.title} - 奶水漫画 ${category.name}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-white/90 text-foreground-800">
                          {article.readTime} 分钟
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h2 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-accent-600 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
                        {article.title}
                      </h2>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.publishDate}</span>
                        <span>{article.viewCount.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}